import { ArrowDropDown } from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import { flatten, keyBy } from "lodash";
import {
  ShipmentEvent,
  ShipmentEventType,
  ShipmentEventInput,
  ShipmentLocationType,
  Status,
} from "../../../graphql/generated";
import StepIndicator from "../../common/StepIndicator";
import { Step } from "../../common/StepIndicator/StepIndicator";
import {
  ShipmentLocationDetails,
  ShipmentLocationTime,
} from "../../shipment/ShipmentSummary/ShipmentSummary";
import ShipmentEventLogButton from "../../shipment/ShipmentEventLogButton";
import { TripSummaryData } from "../TripPlanner/TripsGenerator";

type TripTimelineData = Omit<TripSummaryData, "shipments"> & {
  shipments: (TripSummaryData["shipments"][0] & {
    events?: (
      Pick<ShipmentEvent, "_id" | "type" | "shipmentLocation"> & {
        tripShipmentLocation?: string | null;
      }
    )[] | null;
  })[];
};

type TripTimelineLocation = TripTimelineData["shipmentLocations"][number];
type TripTimelineEvent = NonNullable<
  TripTimelineData["shipments"][number]["events"]
>[number];

const eventMatchesLocation = (
  event: TripTimelineEvent,
  location: TripTimelineLocation,
) =>
  (!!event.tripShipmentLocation &&
    String(event.tripShipmentLocation) ===
      String(location.tripShipmentLocationId)) ||
  (!!event.shipmentLocation &&
    (String(event.shipmentLocation) === String(location._id) ||
      (location.shipmentLocationIds || []).some(
        (shipmentLocationId) =>
          String(shipmentLocationId) === String(event.shipmentLocation),
      )));

export interface TripTimelineProps {
  trip: TripTimelineData;
  horizontal?: boolean;
  showProgress?: boolean;
  onShipmentEventLog: (event: ShipmentEventInput) => void;
}

export enum LoadLocationStatus {
  NOT_STARTED = "NOT_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETE = "COMPLETE",
}

export function getLocationStatusFromTrip(
  trip: TripTimelineData,
  location: TripTimelineLocation,
): LoadLocationStatus {
  const locationEvents = flatten(
    trip.shipments.map((shipment) => shipment.events || []),
  ).filter((event) => eventMatchesLocation(event, location));

  if (
    locationEvents.find(
      (event) =>
        event.type === ShipmentEventType.PickupCheckpointLeft ||
        event.type === ShipmentEventType.DropOffCheckpointLeft,
    )
  ) {
    return LoadLocationStatus.COMPLETE;
  }
  if (
    locationEvents.find(
      (event) =>
        event.type === ShipmentEventType.PickupCheckpointLoadStart ||
        event.type === ShipmentEventType.DropoffCheckpointUnloadStart ||
        event.type === ShipmentEventType.PickupCheckpointArrive ||
        event.type === ShipmentEventType.DropOffCheckpointArrive,
    )
  ) {
    return LoadLocationStatus.IN_PROGRESS;
  }

  return LoadLocationStatus.NOT_STARTED;
}

function TripTimeline({
  trip,
  horizontal,
  showProgress,
  onShipmentEventLog,
}: TripTimelineProps) {
  const theme = useTheme();
  const allShippedGoodsById = keyBy(
    flatten(
      trip.shipmentLocations.map((location) => location.shippedGoods || []),
    ),
    "_id",
  );
  const currentTripLocation = getTripCurrentLocation(trip);
  const nextTripLocation = getTripNextLocation(trip);
  const steps: Step[] = trip.shipmentLocations.map(
    (tripShipmentLocation, index) => {
      const locationId = tripShipmentLocation._id;

      const isCurrent = currentTripLocation === tripShipmentLocation;
      const isNext = nextTripLocation === tripShipmentLocation;
      const status = getLocationStatusFromTrip(trip, tripShipmentLocation);

      return {
        id: locationId,
        header: horizontal ? (
          <Box
            sx={{
              height: showProgress ? 80 : "auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "end",
              alignItems: "center",
            }}
          >
            {showProgress && (isNext || isCurrent) ? (
              <>
                <Typography variant="h6">
                  {isCurrent ? "Current Stop" : "Next Stop"}
                </Typography>
                <ArrowDropDown color="primary" fontSize="large" />
              </>
            ) : null}
            <Box
              sx={{ ml: 2, height: horizontal ? 15 : undefined }}
              textAlign="right"
            >
              <ShipmentLocationTime shipmentLocation={tripShipmentLocation} />
            </Box>
          </Box>
        ) : null,
        content: (
          <Box>
            <ShipmentLocationDetails
              allShippedGoodsById={allShippedGoodsById}
              shipmentLocation={tripShipmentLocation}
              hideTime={horizontal}
              centered={horizontal}
            />
            {!horizontal ? (
              <Box sx={{ mt: 1 }}>
                <ShipmentEventLogButton
                  shipmentLocation={tripShipmentLocation}
                  onShipmentEventLog={(eventType) => {
                    // Get effective shipment IDs for backward compatibility
                    const shipmentIds = tripShipmentLocation.shipmentIds?.length
                      ? tripShipmentLocation.shipmentIds
                      : tripShipmentLocation.shipmentId
                      ? [tripShipmentLocation.shipmentId]
                      : [];

                    if (shipmentIds.length === 0) {
                      return;
                    }

                    // Use first shipment ID for backward compatibility with event logging
                    // Backend will handle applying event to all shipments at this location
                    onShipmentEventLog({
                      type: eventType,
                      trip: trip._id,
                      shipment: shipmentIds[0],
                      shipmentLocation: tripShipmentLocation._id,
                      tripShipmentLocation:
                        tripShipmentLocation.tripShipmentLocationId,
                      location: {
                        latitude: 0,
                        longitude: 0,
                      },
                    });
                  }}
                />
              </Box>
            ) : null}
          </Box>
        ),
        color:
          status === LoadLocationStatus.COMPLETE
            ? "gray"
            : tripShipmentLocation.locationType === ShipmentLocationType.Pickup
            ? theme.palette.primary.main
            : theme.palette.accent.main,
        separatorLength: 40,
        hideFirstLine: index === 0,
        hideLastLine: index === trip.shipmentLocations.length - 1,
      };
    },
  );
  return (
    <Box>
      <StepIndicator steps={steps} horizontal={horizontal} />
    </Box>
  );
}

export const getTripNextLocation = (trip: TripTimelineData) => {
  if (trip.status === Status.Complete) {
    return null;
  }
  const allEvents = flatten(
    trip.shipments.map((shipment) => shipment.events || []),
  );
  return trip.shipmentLocations.find(
    (tripShipmentLocation) =>
      !allEvents.some(
        (event) =>
          eventMatchesLocation(event, tripShipmentLocation) &&
          (event.type === ShipmentEventType.PickupCheckpointArrive ||
            event.type === ShipmentEventType.DropOffCheckpointArrive ||
            event.type === ShipmentEventType.PickupCheckpointLeft ||
            event.type === ShipmentEventType.DropOffCheckpointLeft),
      ),
  );
};

export const getTripCurrentLocation = (trip: TripTimelineData) => {
  const allEvents = flatten(
    trip.shipments.map((shipment) => shipment.events || []),
  );
  return trip.shipmentLocations.find(
    (tripShipmentLocation) =>
      allEvents.some(
        (event) =>
        eventMatchesLocation(event, tripShipmentLocation) &&
          (event.type === ShipmentEventType.PickupCheckpointArrive ||
            event.type === ShipmentEventType.DropOffCheckpointArrive),
      ) &&
      !allEvents.some(
        (event) =>
        eventMatchesLocation(event, tripShipmentLocation) &&
          (event.type === ShipmentEventType.PickupCheckpointLeft ||
            event.type === ShipmentEventType.DropOffCheckpointLeft),
      ),
  );
};

export default TripTimeline;
